import React, { useEffect, useState } from 'react';
import { researchToolsApi } from '../api';
import type { ExperimentDetail } from '../types';
import '../research-tools-overlay.css';

interface Props {
  isOwner: boolean;
  onBack: () => void;
  onOpenCreate: () => void;
}

const STATUS_LABEL: Record<string, string> = {
  DRAFT: 'Черновик',
  MODERATION: 'На модерации',
  APPROVED: 'Одобрен',
};

/** Experiment registry — list + approve + guest link (research-tools shell). */
export const ExperimentRegistryView: React.FC<Props> = ({
  isOwner,
  onBack,
  onOpenCreate,
}) => {
  const [experiments, setExperiments] = useState<ExperimentDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const loadExperiments = () => {
    setLoading(true);
    researchToolsApi
      .getExperiments(statusFilter)
      .then(setExperiments)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadExperiments();
  }, [statusFilter]);

  const handleApprove = async (id: string) => {
    try {
      await researchToolsApi.updateExperimentStatus(id, 'APPROVED');
      loadExperiments();
      alert('Эксперимент успешно одобрен и опубликован');
    } catch {
      alert('Ошибка при одобрении эксперимента');
    }
  };

  const handleCopyLink = (token: string, id: string) => {
    const url = `${window.location.origin}/guest/experiment/${token}`;
    navigator.clipboard.writeText(url);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div>
      <section className="research-tools-hero">
        <div>
          <small>Zertteu Academy · исследовательский контур</small>
          <h2>Провести эксперимент</h2>
          <p>
            Реестр сценариев: черновик → модерация → одобрение. Гостевая ссылка появляется после
            одобрения.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <button type="button" className="research-tools-btn" onClick={onBack}>
            ← Назад
          </button>
          <button type="button" className="research-tools-btn primary" onClick={onOpenCreate}>
            + Создать эксперимент
          </button>
        </div>
      </section>

      <section className="research-tools-panel">
        <header>
          <div>
            <h3>Эксперименты</h3>
            <p>Черновик → модерация → одобрен</p>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {[
              ['ALL', 'Все'],
              ['DRAFT', 'Черновик'],
              ['MODERATION', 'Модерация'],
              ['APPROVED', 'Одобрен'],
            ].map(([code, label]) => (
              <button
                key={code}
                type="button"
                className="research-tools-btn"
                style={
                  statusFilter === code
                    ? { background: '#e2ad3a', color: '#07131b', borderColor: '#e2ad3a' }
                    : undefined
                }
                onClick={() => setStatusFilter(code)}
              >
                {label}
              </button>
            ))}
          </div>
        </header>
        <div className="research-tools-panel-body">
          {loading ? (
            <div style={{ color: '#84a4b4', textAlign: 'center', padding: '1.5rem' }}>
              Загрузка экспериментов…
            </div>
          ) : experiments.length === 0 ? (
            <div style={{ color: '#84a4b4', textAlign: 'center', padding: '2rem 1rem' }}>
              <p>Пока нет созданных экспериментов.</p>
              <button
                type="button"
                className="research-tools-btn primary"
                style={{ marginTop: 12 }}
                onClick={onOpenCreate}
              >
                Создать первый эксперимент
              </button>
            </div>
          ) : (
            <div className="research-tools-survey-list">
              {experiments.map((exp) => {
                const accent =
                  exp.status === 'APPROVED'
                    ? '#49ddb7'
                    : exp.status === 'MODERATION'
                      ? '#e2ad3a'
                      : '#41d2ff';
                const guestUrl = `${window.location.origin}/guest/experiment/${exp.guest_token}`;
                return (
                  <article
                    key={exp.id}
                    className="research-tools-survey-row"
                    style={{ ['--s' as string]: accent }}
                  >
                    <div>
                      <strong>{exp.title}</strong>
                      <small>
                        {exp.experiment_type} · {exp.stimuli_questions.length} стимулов ·{' '}
                        {exp.responses_count} ответов
                      </small>
                      {exp.status === 'APPROVED' ? (
                        <div className="research-tools-public-link">
                          <code>{guestUrl}</code>
                          <button
                            type="button"
                            className="research-tools-btn"
                            onClick={() => handleCopyLink(exp.guest_token, exp.id)}
                          >
                            {copiedId === exp.id ? 'Скопировано' : 'Копировать'}
                          </button>
                        </div>
                      ) : null}
                    </div>
                    <div>
                      <span className="research-tools-badge">
                        {STATUS_LABEL[exp.status] || exp.status}
                      </span>
                      {isOwner && exp.status === 'MODERATION' ? (
                        <button
                          type="button"
                          className="research-tools-btn primary"
                          style={{ marginTop: 7, display: 'block' }}
                          onClick={() => handleApprove(exp.id)}
                        >
                          Одобрить
                        </button>
                      ) : null}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};
